import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Separator } from "../components/ui/separator";
import { ArrowUpRight, Mouse, Crosshair, Target } from "lucide-react";

const PANELS = {
  peripherals: {
    title: "Peripherals",
    kicker: "Hardware · 2026 rig",
    icon: Mouse,
    rows: [
      ["Mouse", "Logitech G Pro X2 Superstrike"],
      ["Mousepad", "Razer Atlas"],
      ["Keyboard", "Logitech G515 Rapid TKL"],
      ["Headset", "SteelSeries Arctis Nova Pro Wireless"],
      ["Monitor", "GIGABYTE GO27Q24"],
      ["DPI", "1600"],
    ],
    external: "https://www.rtings.com/mouse",
  },
  valorant: {
    title: "Valorant Settings",
    kicker: "Episode 10 · competitive",
    icon: Crosshair,
    rows: [
      ["Sensitivity", "0.2 @ 1600 DPI (eDPI 320)"],
      ["Scoped Sens", "1.00"],
      ["Resolution", "2560×1440 or 1920×1440"],
      ["FOV / Aspect", "Native 16:9"],
      ["Crosshair", "0;s;1;P;o;1;d;1;f;0;0b;0;1b;0;S;s;0.8;o;1"],
      ["Minimap", "Rotate · fixed · size 1.15"],
    ],
    external: "https://playvalorant.com/",
  },
  cs2: {
    title: "CS2 Settings",
    kicker: "Premier · active duty",
    icon: Target,
    rows: [
      ["Sensitivity", "0.62 @ 1600 DPI"],
      ["Zoom Sensitivity", "1.00"],
      ["Resolution", "1920×1440 · stretched"],
      ["Raw Input", "On · Mouse Accel Off"],
      ["Crosshair", 'cl_crosshairstyle "4" cl_crosshairsize "2" cl_crosshairthickness "2" cl_crosshairgap "-2" cl_crosshaircolor "1" cl_crosshaircolor_r "0" cl_crosshaircolor_g "255" cl_crosshaircolor_b "0" cl_crosshairdot "0" cl_crosshair_drawoutline "0" cl_crosshairalpha "255"'],
      ["Launch Options", "-windowed fullscreen -w 1920 -h 1440 -refresh 240 +fps_max 0 -high +cl_forcepreload 1 -threads 9 -allow_third_party_software +r_dynamic 0"],
    ],
    external: "https://www.counter-strike.net/",
  },
};

const TriggerCard = ({ panelKey, onOpen, panel }) => {
  const Icon = panel.icon;
  return (
    <button
      data-testid={`open-${panelKey}-btn`}
      onClick={() => onOpen(panelKey)}
      className="group relative text-left overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-5 backdrop-blur-md transition-all duration-500 hover:-translate-y-1 hover:border-cyan-300/40 hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60"
    >
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-cyan-200/70">
            {panel.kicker}
          </div>
          <div
            className="mt-2 text-2xl text-white"
            style={{ fontFamily: "'Instrument Serif', serif", letterSpacing: "-0.01em" }}
          >
            {panel.title}
          </div>
        </div>
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 bg-slate-900/40 text-cyan-200 transition-transform duration-500 group-hover:rotate-[-8deg] group-hover:border-cyan-300/50">
          <Icon size={18} strokeWidth={1.5} />
        </div>
      </div>

      <div className="mt-6 flex items-center gap-2 text-xs text-slate-300/80">
        <span className="h-px flex-1 bg-gradient-to-r from-cyan-300/40 to-transparent" />
        <span className="font-mono tracking-wider">OPEN</span>
        <ArrowUpRight size={14} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </div>
    </button>
  );
};

const DetailDialog = ({ openKey, onClose }) => {
  const panel = openKey ? PANELS[openKey] : null;
  if (!panel) return null;
  const Icon = panel.icon;

  return (
    <Dialog open={!!openKey} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid={`${openKey}-dialog`}
        className="max-w-lg border-white/10 bg-slate-950/80 text-slate-100 backdrop-blur-xl"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-cyan-300/30 bg-cyan-300/5 text-cyan-200">
              <Icon size={18} strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-200/70">
                {panel.kicker}
              </div>
              <DialogTitle
                className="text-2xl text-white"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                {panel.title}
              </DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-slate-300/80">
            Current configuration. Opens the reference page in a new tab for the full breakdown.
          </DialogDescription>
        </DialogHeader>

        <Separator className="bg-white/10" />

        <dl className="divide-y divide-white/5">
          {panel.rows.map(([k, v]) => (
            <div key={k} className="grid grid-cols-5 gap-4 py-2.5">
              <dt className="col-span-2 font-mono text-xs uppercase tracking-wider text-cyan-200/70">
                {k}
              </dt>
              <dd className="col-span-3 text-sm text-slate-100">{v}</dd>
            </div>
          ))}
        </dl>

        <DialogFooter className="sm:justify-between">
          <div className="font-mono text-[11px] text-slate-400/70">
            Updated · Feb 2026
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const Landing = () => {
  const [openKey, setOpenKey] = useState(null);

  return (
    <main
      data-testid="landing-main"
      className="relative min-h-screen w-full px-6 py-16 sm:px-12 lg:px-20"
    >
      {/* hero */}
      <section className="mx-auto mt-24 grid max-w-6xl grid-cols-1 gap-16 lg:mt-28 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <p
            data-testid="hero-sub"
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-200/85 sm:text-lg"
          >
            KaZ&apos;s Settings
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#panels"
              data-testid="secondary-cta"
              className="font-mono text-xs uppercase tracking-[0.24em] text-slate-200/80 underline-offset-8 hover:text-cyan-200 hover:underline"
            >
              browse specs
            </a>
          </div>
        </div>

        {/* ambient data strip */}
        <aside className="relative lg:col-span-5" />
      </section>

      {/* panels */}
      <section id="panels" className="mx-auto mt-28 max-w-6xl">
        <div className="flex items-end justify-between gap-6">
          <div>
            <h2
              className="mt-3 text-3xl text-white sm:text-4xl"
              style={{ fontFamily: "'Instrument Serif', serif", letterSpacing: "-0.01em" }}
            >
              Here it is....
            </h2>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(PANELS).map(([key, panel]) => (
            <TriggerCard key={key} panelKey={key} panel={panel} onOpen={setOpenKey} />
          ))}
        </div>
      </section>

      {/* footer */}
      <footer className="mx-auto mt-32 flex max-w-6xl items-center justify-between font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400/70">
      </footer>

      <DetailDialog openKey={openKey} onClose={() => setOpenKey(null)} />
    </main>
  );
};

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-cyan-200/70">{k}</span>
    <span className="text-slate-100">{v}</span>
  </div>
);

export default Landing;
